import type { GameDTO } from '@pickem/shared';
import { formatKickoffTime } from '../lib/format';

/**
 * A single matchup with two large tap targets. Selection is communicated with
 * more than color (a filled state, bold weight, and an explicit "Your pick"
 * check) for accessibility.
 */
export function MatchupCard({
  game,
  index,
  selectedTeamId,
  onSelect,
  disabled,
  isTiebreaker,
}: {
  game: GameDTO;
  index: number;
  selectedTeamId: string | null;
  onSelect: (teamId: string) => void;
  disabled?: boolean;
  isTiebreaker?: boolean;
}) {
  return (
    <fieldset
      className="card overflow-hidden p-0"
      aria-labelledby={`game-${game.id}-legend`}
    >
      <legend id={`game-${game.id}-legend`} className="sr-only">
        Game {index + 1}: {game.awayTeam.displayName} at {game.homeTeam.displayName}
      </legend>
      <div className="flex items-center justify-between border-b border-line bg-cream/60 px-4 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted">
          Game {index + 1}
        </span>
        <span className="flex items-center gap-2 text-xs text-muted">
          {isTiebreaker && (
            <span className="chip bg-amber/20 text-amber-dark">Sunday Night</span>
          )}
          {formatKickoffTime(game.kickoff)}
        </span>
      </div>

      <div role="radiogroup" aria-label="Pick the winner" className="grid grid-cols-1 divide-y divide-line">
        <TeamChoice
          label="Away"
          teamId={game.awayTeam.id}
          location={game.awayTeam.location}
          name={game.awayTeam.name}
          abbr={game.awayTeam.abbreviation}
          record={game.awayTeam.record}
          selected={selectedTeamId === game.awayTeam.id}
          onSelect={onSelect}
          disabled={disabled}
        />
        <TeamChoice
          label="Home"
          teamId={game.homeTeam.id}
          location={game.homeTeam.location}
          name={game.homeTeam.name}
          abbr={game.homeTeam.abbreviation}
          record={game.homeTeam.record}
          selected={selectedTeamId === game.homeTeam.id}
          onSelect={onSelect}
          disabled={disabled}
        />
      </div>
    </fieldset>
  );
}

function TeamChoice({
  label,
  teamId,
  location,
  name,
  abbr,
  record,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  teamId: string;
  location: string;
  name: string;
  abbr: string;
  record?: string;
  selected: boolean;
  onSelect: (id: string) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={() => onSelect(teamId)}
      className={`flex w-full items-center gap-3 px-4 py-4 text-left transition-colors
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber
        disabled:cursor-not-allowed
        ${selected ? 'bg-grove text-white' : 'bg-surface hover:bg-cream'}`}
    >
      <span
        aria-hidden
        className={`flex h-11 w-11 flex-none items-center justify-center rounded font-display text-sm tracking-wide
          ${selected ? 'bg-white/15 text-white' : 'bg-grove/10 text-grove'}`}
      >
        {abbr}
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-xs uppercase tracking-wide ${selected ? 'text-white/70' : 'text-muted'}`}>
          {label}
        </span>
        <span className="block truncate text-base font-bold leading-tight">
          {location} {name}
        </span>
        {record && (
          <span className={`block text-xs tabular-nums ${selected ? 'text-white/70' : 'text-muted'}`}>
            <span className="sr-only">Record </span>
            {record}
          </span>
        )}
      </span>
      <span
        className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border
          ${selected ? 'border-white bg-white text-grove' : 'border-line text-transparent'}`}
        aria-hidden
      >
        ✓
      </span>
      {selected && <span className="sr-only">Your pick</span>}
    </button>
  );
}
